import { isUndefined } from 'ramda-adjunct'
import { computed, readonly, ref, Ref, unref, watchEffect } from 'vue'

import { ReadonlyRefOrRaw } from '../types/RefOrRaw'
import { animationFrameInterval } from '../utils/animationFrameInterval'

import { useHTMLMediaElementEventListener } from './useEventListener'

const useAudioDuration = (
  elRefOrRaw: ReadonlyRefOrRaw<HTMLAudioElement | undefined>,
): Readonly<Ref<number>> => {
  const duration = ref(0)

  watchEffect(() => {
    const audioEl = unref(elRefOrRaw)
    if (isUndefined(audioEl)) return
    if (isNaN(audioEl.duration)) return
    duration.value = audioEl.duration
  })

  useHTMLMediaElementEventListener(elRefOrRaw, 'loadedmetadata', () => {
    const audioEl = unref(elRefOrRaw)
    if (isUndefined(audioEl)) return
    duration.value = audioEl.duration
  })
  return readonly(duration)
}

const useAudioCurrentTime = (
  elRefOrRaw: ReadonlyRefOrRaw<HTMLAudioElement | undefined>,
): Readonly<Ref<number>> => {
  const state = useAudioState(elRefOrRaw)
  const currentTime = ref(0)
  const duration = useAudioDuration(elRefOrRaw)

  watchEffect((onCleanup) => {
    const audioEl = unref(elRefOrRaw)
    if (state.value === 'UNSTARTED') {
      currentTime.value = 0
      return
    }

    if (isUndefined(audioEl))
      throw new Error(`expected audioEl to be defined because it's state is not UNSTARTED.`)

    if (state.value === 'ENDED') {
      currentTime.value = duration.value
      return
    }

    if (state.value !== 'PLAYING') return

    const cancel = animationFrameInterval(() => {
      currentTime.value = audioEl.currentTime
    })

    onCleanup(cancel)
  })

  return readonly(currentTime)
}

export const useAudioRemainingSeconds = (
  elRefOrRaw: ReadonlyRefOrRaw<HTMLAudioElement | undefined>,
): Readonly<Ref<number>> => {
  const currentTime = useAudioCurrentTime(elRefOrRaw)
  const duration = useAudioDuration(elRefOrRaw)

  return computed(() => Math.round(duration.value - currentTime.value))
}

export const useAudioProgress = (
  elRefOrRaw: ReadonlyRefOrRaw<HTMLAudioElement | undefined>,
): Readonly<Ref<number>> => {
  const currentTime = useAudioCurrentTime(elRefOrRaw)
  const duration = useAudioDuration(elRefOrRaw)

  return computed(() => {
    if (duration.value <= 0) return 0
    return currentTime.value / duration.value
  })
}

export type AudioState = 'UNSTARTED' | 'PAUSED' | 'PLAYING' | 'ENDED'
export const useAudioState = (
  elRefOrRaw: ReadonlyRefOrRaw<HTMLAudioElement | undefined>,
): Readonly<Ref<AudioState>> => {
  const state = ref<AudioState>('UNSTARTED')

  useHTMLMediaElementEventListener(elRefOrRaw, 'pause', () => {
    state.value = 'PAUSED'
  })

  useHTMLMediaElementEventListener(elRefOrRaw, 'play', () => {
    state.value = 'PLAYING'
  })

  useHTMLMediaElementEventListener(elRefOrRaw, 'ended', () => {
    state.value = 'ENDED'
  })

  watchEffect(() => {
    const audioEl = unref(elRefOrRaw)
    if (!audioEl) {
      state.value = 'UNSTARTED'
      return
    }

    state.value = audioEl.paused ? 'PAUSED' : 'PLAYING'
  })

  return readonly(state)
}
